import * as React from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { request } from "../../../../services/cpln";
import { useAppSelector } from "../../../../store/hooks";
import { selectLabel, selectValue } from "../../../../store/timezoneSlice";
import { BaseChart } from "../../BaseChart";
import {
  QueryRangeResponse,
  ChartDataPoint,
  getTimeRange,
  formatCount,
  getMetricsErrorMessage,
  buildByClause,
  translateGrouping,
  getAdaptiveTimeFormat,
} from "../../utils";

dayjs.extend(utc);
dayjs.extend(timezone);

const EMPTY_ARRAY: string[] = [];

export interface AgentPeeringChartProps {
  org: string;
  grouping?: string[];
  hideFromLabel?: string[];
  fromIso?: string | null;
  toIso?: string | null;
  step?: string;
}

export const AgentPeeringChart: React.FC<AgentPeeringChartProps> = ({
  org,
  grouping = EMPTY_ARRAY,
  hideFromLabel = EMPTY_ARRAY,
  fromIso,
  toIso,
  step = "30s",
}) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [chartData, setChartData] = React.useState<ChartDataPoint[]>([]);
  const [seriesKeys, setSeriesKeys] = React.useState<string[]>([]);

  const timezoneValue = useAppSelector(selectValue);
  const timezoneLabel = useAppSelector(selectLabel);

  React.useEffect(() => {
    if (org) {
      fetchMetrics();
    }
  }, [org, grouping, fromIso, toIso, step, timezoneValue]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      const translatedGrouping = translateGrouping(grouping, "agent_name");
      const byClause = buildByClause(translatedGrouping);
      const peersQuery = `sum(agent_peers_count{}) ${byClause}`;
      const servicesQuery = `sum(agent_services_count{}) ${byClause}`;
      const hasItemGrouping = grouping.includes("item");

      const { start, end } = getTimeRange(fromIso, toIso);

      const [peersResponse, servicesResponse] = await Promise.all([
        request<QueryRangeResponse>({
          service: "metrics",
          method: "post",
          url: `/metrics/org/${org}/api/v1/query_range?query=${encodeURIComponent(peersQuery)}&start=${start.toISOString()}&end=${end.toISOString()}&step=${step}`,
        }),
        request<QueryRangeResponse>({
          service: "metrics",
          method: "post",
          url: `/metrics/org/${org}/api/v1/query_range?query=${encodeURIComponent(servicesQuery)}&start=${start.toISOString()}&end=${end.toISOString()}&step=${step}`,
        }),
      ]);

      const dataMap = new Map<number, ChartDataPoint>();
      const keysSet = new Set<string>();

      // Build series key based on grouping
      const buildSeriesKey = (metric: Record<string, string>, suffix: string) => {
        if (!hasItemGrouping) return suffix;
        const groupingPart = translatedGrouping
          .filter((g) => !hideFromLabel.includes(g))
          .map((g) => metric[g] || "unknown")
          .filter(Boolean)
          .join("/");
        return groupingPart ? `${groupingPart} (${suffix})` : suffix;
      };

      // Process Peers data
      if (peersResponse.data.status === "success" && peersResponse.data.data?.result) {
        for (const series of peersResponse.data.data.result) {
          const key = buildSeriesKey(series.metric, "Peers");
          keysSet.add(key);

          for (const [timestamp, value] of series.values) {
            const ts = timestamp * 1000;
            if (!dataMap.has(ts)) {
              dataMap.set(ts, {
                timestamp: ts,
                formattedTime: "",
              });
            }
            const point = dataMap.get(ts)!;
            point[key] = (point[key] as number || 0) + parseFloat(value);
          }
        }
      }

      // Process Services data
      if (servicesResponse.data.status === "success" && servicesResponse.data.data?.result) {
        for (const series of servicesResponse.data.data.result) {
          const key = buildSeriesKey(series.metric, "Services");
          keysSet.add(key);

          for (const [timestamp, value] of series.values) {
            const ts = timestamp * 1000;
            if (!dataMap.has(ts)) {
              dataMap.set(ts, {
                timestamp: ts,
                formattedTime: "",
              });
            }
            const point = dataMap.get(ts)!;
            point[key] = (point[key] as number || 0) + parseFloat(value);
          }
        }
      }

      const processedData = Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);
      const timeFormat = getAdaptiveTimeFormat(processedData);
      processedData.forEach((point) => {
        point.formattedTime = dayjs(point.timestamp).tz(timezoneValue).format(timeFormat);
      });
      const keys = Array.from(keysSet);

      setChartData(processedData);
      setSeriesKeys(keys);
    } catch (err: any) {
      console.error("Failed to fetch metrics:", err);
      setError(getMetricsErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseChart
      title="Agent Peering"
      loading={loading}
      error={error}
      chartData={chartData}
      seriesKeys={seriesKeys}
      formatYAxis={formatCount}
      formatTooltip={(value) => value.toFixed(0)}
      timezoneLabel={timezoneLabel}
    />
  );
};
